import {
    createClientConfig,
    createStaticConfigProvider,
    libConfig,
    uiPositiveIntegerRuntimeConfigSettings
} from '@sstdev/lib_config';

const NATIVE_RUNTIME_DEFAULTS = {
    maxRecordsInLimitedSizeCollection: 20000
};

/**
 * Resolves the UI runtime config values that are safe on both browser and native.
 *
 * `globalConfig()` still owns the compatibility surface. This adapter only
 * centralizes values that already behave like runtime config, so callers can
 * keep using `globalConfig()` while new sources move behind `lib/config`.
 *
 * @param {object} [options]
 * @param {Record<string, string | undefined>} [options.env] Env-like source used by server/native callers and tests.
 * @param {Record<string, unknown>} [options.globalObject] Browser or native global object used for injected runtime values.
 * @param {'web' | 'native'} [options.platform] Runtime family used for platform-specific defaults.
 * @param {string[]} [options.sessionFeatureFlags] Use-case/session feature flags added after deployment-wide flags.
 * @returns {UiRuntimeConfigSnapshot}
 */
export function getUiRuntimeConfig({
    env = getDefaultEnv(),
    globalObject = getDefaultGlobalObject(),
    platform = getDefaultPlatform(globalObject),
    sessionFeatureFlags = []
} = {}) {
    // Build the manager per call because globalConfig() can be intentionally
    // reinitialized after session flags or test globals change.
    const { getConfig } = createClientConfig({
        env,
        fallbackProviders: [
            createStaticConfigProvider({
                name: 'ui-platform-defaults',
                values: createPlatformDefaultValues(platform)
            })
        ],
        globalObject
    });

    const positiveIntegerSettings = resolvePositiveIntegerRuntimeSettings(getConfig);

    return {
        featureFlags: mergeFeatureFlags(getConfig(libConfig.ui.featureFlags), sessionFeatureFlags),
        ...positiveIntegerSettings
    };
}

export const UI_RUNTIME_CONFIG_PROPERTIES = Object.freeze([
    'featureFlags',
    ...uiPositiveIntegerRuntimeConfigSettings.map(setting => setting.property)
]);

/**
 * Returns the legacy globalConfig() keys for settings backed by lib/config.
 *
 * Keeping this conversion here lets initialize() stay focused on assembling the
 * public config object instead of knowing how each setting is resolved.
 *
 * @param {<T>(definition: import('@sstdev/lib_config').ConfigDefinition<T>) => T} getConfig
 * @returns {Omit<UiRuntimeConfigSnapshot, 'featureFlags'>}
 */
function resolvePositiveIntegerRuntimeSettings(getConfig) {
    return /** @type {Omit<UiRuntimeConfigSnapshot, 'featureFlags'>} */ (Object.fromEntries(
        uiPositiveIntegerRuntimeConfigSettings.map(setting => [
            setting.property,
            getConfig(libConfig.ui.runtime[setting.property])
        ])
    ));
}

/**
 * @param {'web' | 'native'} platform
 */
function createPlatformDefaultValues(platform) {
    if (platform !== 'native') {
        return {};
    }

    return Object.fromEntries(
        Object.entries(NATIVE_RUNTIME_DEFAULTS).map(([property, value]) => [
            libConfig.ui.runtime[property].key,
            value
        ])
    );
}

/**
 * @param {unknown[]} runtimeFeatureFlags
 * @param {string[]} sessionFeatureFlags
 */
function mergeFeatureFlags(runtimeFeatureFlags, sessionFeatureFlags) {
    // Runtime feature flags are deployment-wide switches. Session/use-case flags
    // add narrower capabilities after login, but should not erase deployment
    // switches such as GLOBAL_FEATURE_FLAGS=gl2025.
    return [...new Set([...normalizeFeatureFlags(runtimeFeatureFlags), ...normalizeFeatureFlags(sessionFeatureFlags)])];
}

/**
 * @param {unknown[]} featureFlags
 * @returns {string[]}
 */
function normalizeFeatureFlags(featureFlags) {
    return featureFlags.map(featureFlag => `${featureFlag}`.trim()).filter(Boolean);
}

/** @returns {Record<string, string | undefined>} */
function getDefaultEnv() {
    const runtimeGlobal = /** @type {{ process?: { env?: Record<string, string | undefined> } }} */ (globalThis);
    return runtimeGlobal.process?.env ?? {};
}

/**
 * React Native does not have browser-injected runtime globals. Platform-specific
 * defaults live in a provider layer after explicit globals/env so tests and
 * future native injection paths can still override them deliberately.
 *
 * @param {Record<string, unknown>} globalObject
 * @returns {'web' | 'native'}
 */
function getDefaultPlatform(globalObject) {
    return globalObject.__SST_REACT_NATIVE__ ? 'native' : 'web';
}

/** @returns {Record<string, unknown>} */
function getDefaultGlobalObject() {
    return typeof globalThis === 'undefined' ? {} : globalThis;
}

/**
 * @typedef {object} UiRuntimeConfigSnapshot
 * @property {string[]} featureFlags
 * @property {number} actionElementDebounceMilliseconds
 * @property {number} auth0RefreshTokenAbsoluteLifetimeDays
 * @property {number} backgroundSyncBatchSize
 * @property {number} getRecordsDebounceMilliseconds
 * @property {number} getRecordsThrottleMilliseconds
 * @property {number} loadingTimeout
 * @property {number} maxDeletePerTruncationPass
 * @property {number} maxRecordsInLimitedSizeCollection
 * @property {number} maxSecondsOfflineWithoutResync
 * @property {number} networkErrorRetryMilliseconds
 * @property {number} notificationTimeout
 * @property {number} notificationTimeoutFormError
 * @property {number} offlineSessionExpirationDays
 * @property {number} onlineSearchDebounceMilliseconds
 * @property {number} pageSizeForLimitSyncSizeRelations
 * @property {number} resyncRandomDelaySeconds
 * @property {number} searchDebounceMilliseconds
 * @property {number} sensorScanIntervalMilliseconds
 */
